import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMe } from '../../hooks/useMe'
import { getToken } from '../../lib/auth'
import { setMyAge } from '../../api/security'

export default function AgeSettings() {
  const navigate = useNavigate()
  const authed = Boolean(getToken())
  const { me, refresh } = useMe(authed)
  const [dateOfBirth, setDateOfBirth] = useState('')
  const [confirm, setConfirm] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)

  const isSet = Boolean(me?.dob_set)
  const ageGroupLabel = useMemo(() => {
    const group = me?.age_group || 'unknown'
    if (group === 'adult') return '18+'
    if (group === 'teen') return '13-17'
    if (group === 'child') return 'Under 13'
    return 'Unknown'
  }, [me?.age_group])

  async function onSave() {
    setSaving(true)
    setError(null)
    setMessage(null)
    try {
      await setMyAge({ date_of_birth: dateOfBirth, confirm })
      await refresh()
      setMessage('Birthday saved.')
      navigate('/settings/account')
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.response?.data?.date_of_birth?.[0] || e?.message || 'Unable to save birthday.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-extrabold">Set Age</h1>
      <p className="text-sm text-white/70">Used to provide age-appropriate content and safety controls.</p>

      {isSet ? (
        <div className="rounded-xl border border-emerald-400/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-100">
          Age set. Current group: {ageGroupLabel}
        </div>
      ) : null}

      <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4 space-y-3">
        <label className="block">
          <div className="mb-1 text-sm text-white/70">Birthday</div>
          <input
            type="date"
            value={dateOfBirth}
            onChange={(e) => setDateOfBirth(e.target.value)}
            max={new Date(Date.now() - 86400000).toISOString().slice(0, 10)}
            className="w-full min-h-11 rounded-lg border border-white/15 bg-black/40 px-3 py-2 outline-none"
          />
        </label>
        <label className="flex items-start gap-2 text-sm text-white/80">
          <input type="checkbox" checked={confirm} onChange={(e) => setConfirm(e.target.checked)} className="mt-0.5" />
          <span>I confirm this is my real birthday.</span>
        </label>
        {error ? <div className="text-sm text-red-300">{error}</div> : null}
        {message ? <div className="text-sm text-emerald-300">{message}</div> : null}
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={onSave}
            disabled={saving || !dateOfBirth || !confirm}
            className="min-h-11 rounded-lg bg-brand px-4 py-2 font-semibold text-white hover:bg-brand-600 disabled:opacity-60"
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
          <button
            type="button"
            onClick={() => navigate('/settings/account')}
            className="min-h-11 rounded-lg border border-white/15 bg-white/[0.03] px-4 py-2 font-semibold text-white hover:bg-white/10"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  )
}

